import React, { useMemo, useCallback } from 'react';
import { Box, Paper, Button, ButtonGroup, Divider } from '@mui/material';
import { LinkPopup } from './LinkPopup';
import {
  FormatBold as FormatBoldIcon,
  FormatItalic as FormatItalicIcon,
  FormatUnderlined as FormatUnderlinedIcon,
  FormatStrikethrough as FormatStrikethroughIcon,
  FormatListBulleted as FormatListBulletedIcon,
  FormatListNumbered as FormatListNumberedIcon,
  FormatAlignLeft as FormatAlignLeftIcon,
  FormatAlignCenter as FormatAlignCenterIcon,
  FormatAlignRight as FormatAlignRightIcon,
  FormatAlignJustify as FormatAlignJustifyIcon,
  Link as LinkIcon,
  Image as ImageIcon,
} from '@mui/icons-material';
import { createEditor, Descendant, BaseEditor, Element as SlateElement, Transforms, Editor, Range } from 'slate';
import { Slate, Editable, withReact, ReactEditor, useSlate } from 'slate-react';

type CustomElement = { 
  type: 'paragraph' | 'list-item' | 'bulleted-list' | 'numbered-list'; 
  children: CustomText[];
  align?: 'left' | 'center' | 'right' | 'justify';
}
type CustomText = { 
  text: string; 
  bold?: boolean; 
  italic?: boolean; 
  underline?: boolean;
  strikethrough?: boolean;
  url?: string;
}

declare module 'slate' {
  interface CustomTypes {
    Editor: BaseEditor & ReactEditor
    Element: CustomElement
    Text: CustomText
  }
}

interface PlateEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}

// Composant pour les boutons de formatage (marks)
const FormatButton = ({ format, icon: Icon, children }: { format: string; icon: any; children?: React.ReactNode }) => {
  const editor = useSlate();
  const isActive = isFormatActive(editor, format);

  const handleClick = (event: React.MouseEvent) => {
    event.preventDefault();
    toggleFormat(editor, format);
  };

  return (
    <Button
      size="small"
      variant={isActive ? 'contained' : 'outlined'}
      onClick={handleClick}
      startIcon={<Icon />}
      sx={{ minWidth: 'auto', px: 1 }}
    >
      {children}
    </Button>
  );
};

// Composant pour les boutons de blocs
const BlockButton = ({ format, icon: Icon, children }: { format: string; icon: any; children?: React.ReactNode }) => {
  const editor = useSlate();
  const isActive = isBlockActive(editor, format);

  const handleClick = (event: React.MouseEvent) => {
    event.preventDefault();
    toggleBlock(editor, format);
  };

  return (
    <Button
      size="small"
      variant={isActive ? 'contained' : 'outlined'}
      onClick={handleClick}
      startIcon={<Icon />}
      sx={{ minWidth: 'auto', px: 1 }}
    >
      {children}
    </Button>
  );
};

// Composant pour les boutons d'alignement
const AlignmentButton = ({ align, icon: Icon, children }: { align: string; icon: any; children?: React.ReactNode }) => {
  const editor = useSlate();
  const isActive = isBlockActive(editor, align);

  const handleClick = (event: React.MouseEvent) => {
    event.preventDefault();
    toggleAlignment(editor, align);
  };

  return (
    <Button
      size="small"
      variant={isActive ? 'contained' : 'outlined'}
      onClick={handleClick}
      startIcon={<Icon />}
      sx={{ minWidth: 'auto', px: 1 }}
    >
      {children}
    </Button>
  );
};

// Composant pour le bouton lien
const LinkButton = () => {
  const editor = useSlate();
  const isActive = isLinkActive(editor);
  const [showLinkPopup, setShowLinkPopup] = React.useState(false);
  const [linkData, setLinkData] = React.useState({ url: '', label: '' });

  const handleClick = (event: React.MouseEvent) => {
    event.preventDefault();
    if (isActive) {
      unwrapLink(editor);
    } else {
      // Récupérer le texte sélectionné comme label par défaut
      const { selection } = editor;
      if (selection) {
        const text = Editor.string(editor, selection);
        setLinkData({ url: '', label: text });
      } else {
        setLinkData({ url: '', label: '' });
      }
      setShowLinkPopup(true);
    }
  };

  const handleInsertLink = () => {
    if (linkData.url.trim()) {
      insertLink(editor, linkData.url, linkData.label);
      setShowLinkPopup(false);
      setLinkData({ url: '', label: '' });
    }
  };

  const handleCancel = () => {
    setShowLinkPopup(false);
    setLinkData({ url: '', label: '' });
  };

  return (
    <>
      <Button
        size="small"
        variant={isActive ? 'contained' : 'outlined'}
        onClick={handleClick}
        startIcon={<LinkIcon />}
        sx={{ minWidth: 'auto', px: 1 }}
      >
        Lien
      </Button>
      
      {/* Popup pour saisir le lien */}
      <LinkPopup
        isOpen={showLinkPopup}
        linkData={linkData}
        onLabelChange={(label) => setLinkData(prev => ({ ...prev, label }))}
        onUrlChange={(url) => setLinkData(prev => ({ ...prev, url }))}
        onCancel={handleCancel}
        onInsert={handleInsertLink}
      />
    </>
  );
};



// Vérifier si un format est actif
const isFormatActive = (editor: any, format: string) => {
  const marks = Editor.marks(editor);
  return marks ? (marks as any)[format] === true : false;
};

// Basculer un format
const toggleFormat = (editor: any, format: string) => {
  const isActive = isFormatActive(editor, format);
  if (isActive) {
    Editor.removeMark(editor, format);
  } else {
    Editor.addMark(editor, format, true);
  }
};

// Vérifier si un bloc est actif
const isBlockActive = (editor: any, format: string) => {
  const { selection } = editor;
  if (!selection) return false;

  const [match] = Editor.nodes(editor, {
    at: Editor.unhangRange(editor, selection),
    match: (n: any) => !Editor.isEditor(n) && SlateElement.isElement(n) && n.type === format,
  });

  return !!match;
};

// Basculer un bloc
const toggleBlock = (editor: any, format: string) => {
  const isActive = isBlockActive(editor, format);
  const isList = format === 'bulleted-list' || format === 'numbered-list';

  Transforms.unwrapNodes(editor, {
    match: (n: any) =>
      !Editor.isEditor(n) &&
      SlateElement.isElement(n) &&
      (n.type === 'bulleted-list' || n.type === 'numbered-list') &&
      !isList,
    split: true,
  });

  let newProperties: Partial<CustomElement>;
  if (isActive) {
    newProperties = { type: 'paragraph' };
  } else if (isList) {
    newProperties = { type: 'list-item' };
  } else {
    newProperties = { type: format as any };
  }

  Transforms.setNodes<CustomElement>(editor, newProperties);

  if (!isActive && isList) {
    const block = { type: format as 'bulleted-list' | 'numbered-list', children: [] };
    Transforms.wrapNodes(editor, block);
  }
};

// Basculer l'alignement
const toggleAlignment = (editor: any, align: string) => {
  Transforms.setNodes(
    editor,
    { align: align as any },
    { match: (n: any) => SlateElement.isElement(n) && Editor.isBlock(editor, n) }
  );
};

// Vérifier si un lien est actif
const isLinkActive = (editor: any) => {
  const marks = Editor.marks(editor);
  return marks ? marks.url !== undefined : false;
};

// Insérer un lien
const insertLink = (editor: any, url: string, label?: string) => {
  if (editor.selection) {
    wrapLink(editor, url, label);
  }
};

// Envelopper le texte sélectionné dans un lien
const wrapLink = (editor: any, url: string, label?: string) => {
  const isLink = isLinkActive(editor);
  if (isLink) {
    unwrapLink(editor);
  }

  const { selection } = editor;
  const isCollapsed = selection && Range.isCollapsed(selection);
  
  if (isCollapsed) {
    // Si pas de sélection, insérer le texte avec le lien
    const linkText = label || url;
    Transforms.insertText(editor, linkText);
    Transforms.select(editor, {
      anchor: { path: selection.anchor.path, offset: selection.anchor.offset },
      focus: { path: selection.anchor.path, offset: selection.anchor.offset + linkText.length }
    });
  }
  
  // Appliquer le mark URL sur la sélection
  Editor.addMark(editor, 'url', url);
};

// Supprimer un lien
const unwrapLink = (editor: any) => {
  Editor.removeMark(editor, 'url');
};

// Convertir HTML string en Slate value
const htmlToSlate = (html: string): Descendant[] => {
  if (!html || html.trim() === '') {
    return [{ type: 'paragraph', children: [{ text: '' }] }];
  }
  
  // Parser le HTML pour gérer les liens
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  const body = doc.body;
  
  const result: Descendant[] = [];
  
  // Traiter chaque élément enfant
  Array.from(body.childNodes).forEach(node => {
    if (node.nodeType === Node.TEXT_NODE) {
      if (node.textContent?.trim()) {
        result.push({ type: 'paragraph', children: [{ text: node.textContent }] });
      }
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      const element = node as Element;
      
      if (element.tagName === 'P') {
        const children: any[] = [];
        Array.from(element.childNodes).forEach(child => {
          if (child.nodeType === Node.TEXT_NODE) {
            if (child.textContent) {
              children.push({ text: child.textContent });
            }
          } else if (child.nodeType === Node.ELEMENT_NODE) {
            const childElement = child as Element;
            if (childElement.tagName === 'A') {
              children.push({
                type: 'link',
                url: childElement.getAttribute('href') || '',
                children: [{ text: childElement.textContent || '' }]
              });
            } else {
              // Gérer les autres balises (strong, em, etc.)
              let text = childElement.textContent || '';
              let marks: any = {};
              
              if (childElement.tagName === 'STRONG') marks.bold = true;
              if (childElement.tagName === 'EM') marks.italic = true;
              if (childElement.tagName === 'U') marks.underline = true;
              if (childElement.tagName === 'S') marks.strikethrough = true;
              
              children.push({ text, ...marks });
            }
          }
        });
        
        if (children.length > 0) {
          result.push({ 
            type: 'paragraph', 
            children,
            align: (element as HTMLElement).style?.textAlign as any || 'left'
          });
        }
      } else if (element.tagName === 'A') {
        // Lien isolé - créer un paragraphe avec le lien comme mark
        const url = element.getAttribute('href') || '';
        const text = element.textContent || '';
        result.push({
          type: 'paragraph',
          children: [{ text, url }]
        });
      }
    }
  });
  
  return result.length > 0 ? result : [{ type: 'paragraph', children: [{ text: '' }] }];
};

// Convertir Slate value en HTML string
const slateToHtml = (value: Descendant[]): string => {
  return value
    .map(n => {
      if (SlateElement.isElement(n)) {
        if (n.type === 'paragraph') {
          const style = n.align && n.align !== 'left' ? ` style="text-align: ${n.align}"` : '';
          return `<p${style}>${n.children.map((child: any) => {
            let text = child.text;
            if (child.bold) text = `<strong>${text}</strong>`;
            if (child.italic) text = `<em>${text}</em>`;
            if (child.underline) text = `<u>${text}</u>`;
            if (child.strikethrough) text = `<s>${text}</s>`;
            if (child.url) {
              text = `<a href="${child.url}" target="_blank" rel="noopener noreferrer">${text}</a>`;
            }
            return text;
          }).join('')}</p>`;
        } else if (n.type === 'list-item') {
          return `<li>${n.children.map((child: any) => {
            let text = child.text;
            if (child.bold) text = `<strong>${text}</strong>`;
            if (child.italic) text = `<em>${text}</em>`;
            if (child.underline) text = `<u>${text}</u>`;
            if (child.strikethrough) text = `<s>${text}</s>`;
            if (child.url) {
              text = `<a href="${child.url}" target="_blank" rel="noopener noreferrer">${text}</a>`;
            }
            return text;
          }).join('')}</li>`;
        } else if (n.type === 'bulleted-list') {
          return `<ul>${n.children.map((child: any) => {
            let text = child.text;
            if (child.bold) text = `<strong>${text}</strong>`;
            if (child.italic) text = `<em>${text}</em>`;
            if (child.underline) text = `<u>${text}</u>`;
            if (child.strikethrough) text = `<s>${text}</s>`;
            if (child.url) {
              text = `<a href="${child.url}" target="_blank" rel="noopener noreferrer">${text}</a>`;
            }
            return text;
          }).join('')}</ul>`;
        } else if (n.type === 'numbered-list') {
          return `<ol>${n.children.map((child: any) => {
            let text = child.text;
            if (child.bold) text = `<strong>${text}</strong>`;
            if (child.italic) text = `<em>${text}</em>`;
            if (child.underline) text = `<u>${text}</u>`;
            if (child.strikethrough) text = `<s>${text}</s>`;
            if (child.url) {
              text = `<a href="${child.url}" target="_blank" rel="noopener noreferrer">${text}</a>`;
            }
            return text;
          }).join('')}</ol>`;
        }
      }
      return '';
    })
    .join('');
};

export const PlateEditor: React.FC<PlateEditorProps> = ({ value, onChange, placeholder }) => {
  const editor = useMemo(() => withReact(createEditor()), []);
  
  // Convertir la valeur HTML en Slate value
  const slateValue = useMemo(() => htmlToSlate(value), [value]);

  const handleChange = useCallback(
    (newValue: Descendant[]) => {
      const html = slateToHtml(newValue);
      onChange(html);
    },
    [onChange]
  );

  const renderElement = useCallback((props: any) => {
    const { attributes, children, element } = props;
    
    switch (element.type) {
      case 'paragraph':
        return (
          <p 
            {...attributes} 
            style={{ 
              textAlign: element.align || 'left',
              direction: 'ltr'
            }}
          >
            {children}
          </p>
        );
      case 'list-item':
        return <li {...attributes}>{children}</li>;
      case 'bulleted-list':
        return <ul {...attributes}>{children}</ul>;
      case 'numbered-list':
        return <ol {...attributes}>{children}</ol>;
      default:
        return <p {...attributes}>{children}</p>;
    }
  }, []);

  const renderLeaf = useCallback((props: any) => {
    let { attributes, children, leaf } = props;

    if (leaf.bold) {
      children = <strong>{children}</strong>;
    }

    if (leaf.italic) {
      children = <em>{children}</em>;
    }

    if (leaf.underline) {
      children = <u>{children}</u>;
    }

    if (leaf.strikethrough) {
      children = <s>{children}</s>;
    }

    if (leaf.url) {
      children = (
        <a href={leaf.url} target="_blank" rel="noopener noreferrer">
          {children}
        </a>
      );
    }

    return <span {...attributes}>{children}</span>;
  }, []);

  return (
    <Box>
      <Paper
        elevation={1}
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          minHeight: '200px',
          direction: 'ltr',
          textAlign: 'left',
        }}
      >
        <Slate
          editor={editor}
          value={slateValue}
          onChange={handleChange}
        >
          {/* Barre d'outils */}
          <Paper
            elevation={0}
            sx={{
              p: 1,
              borderBottom: '1px solid',
              borderColor: 'divider',
              borderRadius: 0,
            }}
          >
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
              {/* Formatage de texte */}
              <ButtonGroup size="small" variant="outlined">
                <FormatButton format="bold" icon={FormatBoldIcon} />
                <FormatButton format="italic" icon={FormatItalicIcon} />
                <FormatButton format="underline" icon={FormatUnderlinedIcon} />
                <FormatButton format="strikethrough" icon={FormatStrikethroughIcon} />
              </ButtonGroup>

              <Divider orientation="vertical" flexItem />

              {/* Alignement */}
              <ButtonGroup size="small" variant="outlined">
                <AlignmentButton align="left" icon={FormatAlignLeftIcon} />
                <AlignmentButton align="center" icon={FormatAlignCenterIcon} />
                <AlignmentButton align="right" icon={FormatAlignRightIcon} />
                <AlignmentButton align="justify" icon={FormatAlignJustifyIcon} />
              </ButtonGroup>

              <Divider orientation="vertical" flexItem />

              {/* Listes */}
              <ButtonGroup size="small" variant="outlined">
                <BlockButton format="bulleted-list" icon={FormatListBulletedIcon} />
                <BlockButton format="numbered-list" icon={FormatListNumberedIcon} />
              </ButtonGroup>

              <Divider orientation="vertical" flexItem />

              {/* Liens et images */}
              <ButtonGroup size="small" variant="outlined">
                <LinkButton />
                <Button size="small" variant="outlined" startIcon={<ImageIcon />} sx={{ minWidth: 'auto', px: 1 }}>
                  Image
                </Button>
              </ButtonGroup>
            </Box>
          </Paper>

          {/* Zone d'édition */}
          <Box sx={{ p: 1 }}>
            <Editable
              renderElement={renderElement}
              renderLeaf={renderLeaf}
              placeholder={placeholder || 'Rédigez votre message ici...'}
              style={{
                minHeight: 180,
                fontFamily: 'inherit',
                fontSize: 15,
                direction: 'ltr',
                textAlign: 'left',
                outline: 'none',
              }}
            />
          </Box>
        </Slate>
      </Paper>
    </Box>
  );
}; 